import React, { createContext, ReactNode, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useViews } from "@/domain/View";

/**
 * 恒等変換（生の値をそのまま返す）。aliasが1件も無い場合の解決関数として再利用し、
 * 参照を固定することで`useAppRouteParams`の不要な再計算を防ぐ。
 */
const identityResolveViewId = (rawViewId: string) => rawViewId;

/**
 * URLのview_idセグメント（aliasまたはドキュメントID）を、ビューのドキュメントIDへ解決する関数。
 * デフォルトは恒等変換（生の値をそのまま返す）。{@link ViewIdResolverProvider}が
 * ビュー文脈（aliasが存在する場合）でのみ実際の解決関数を提供する。
 *
 * @see useAppRouteParams - この解決関数を通してviewIdを返す
 */
export const ViewIdResolverContext = createContext<
  (rawViewId: string) => string
>(identityResolveViewId);

/**
 * @description
 * URLの`view_id`セグメントに指定されたalias/ドキュメントIDを、実際のビュー・ドキュメントIDへ
 * 解決するプロバイダ。InOrganizationProvider内に組み込まれ、配下のレイアウト（サイドナビを含む
 * AppBaseLayout）やページに解決済みviewIdを提供する（ビュー文脈以外では恒等変換となり無害）。
 *
 * - URLがaliasの場合: alias→ドキュメントIDへ解決し、配下の`useAppRouteParams().viewId`が
 *   常にドキュメントIDを返すようにする（ビュー配下のFirestoreサブコレクション参照や
 *   セッションキーがドキュメントID前提のため）。
 * - URLがドキュメントIDで、かつそのビューにaliasが設定されている場合: alias付きURLへ
 *   リダイレクトし、aliasをURL上の正規表現とする（クエリパラメータは維持）。
 *
 * version_idの無いルートでは`useViews`は購読をスキップして空配列を返すため、
 * aliasToIdMapが空＝恒等変換となり、ビュー以外のルートへの影響はない。
 */
export const ViewIdResolverProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  // NOTE: 解決前の生のURLパラメータを参照する（useAppRouteParamsを使うと解決が再帰するため）
  const params = useParams<"organization_id" | "version_id" | "view_id">();
  const rawViewId = params.view_id ?? "";
  const location = useLocation();
  const navigate = useNavigate();

  const [views] = useViews({});

  // alias → ドキュメントID のマップ（空のaliasは対象外）
  const aliasToIdMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const view of views) {
      if (view.alias) {
        map.set(view.alias, view.id);
      }
    }
    return map;
  }, [views]);

  // aliasが1件も無い場合は固定の恒等関数を返し、context値の参照を安定させる
  // （useAppRouteParamsは全ルートで使われるため、毎レンダリングで参照が変わると再計算が広がる）
  const resolveViewId = useMemo(
    () =>
      aliasToIdMap.size === 0
        ? identityResolveViewId
        : (target: string) => aliasToIdMap.get(target) ?? target,
    [aliasToIdMap]
  );

  // ドキュメントIDでアクセスされ、かつaliasを持つビューはalias付きURLへリダイレクトする
  const viewAccessedById = useMemo(
    () => views.find((view) => view.id === rawViewId),
    [views, rawViewId]
  );

  const isViewDisplayRoute = location.pathname.endsWith(`/views/${rawViewId}`);
  const redirectTarget =
    isViewDisplayRoute &&
    viewAccessedById?.alias &&
    viewAccessedById.alias !== rawViewId
      ? `${location.pathname.replace(
          `/views/${rawViewId}`,
          `/views/${viewAccessedById.alias}`
        )}${location.search}`
      : null;

  useEffect(() => {
    if (redirectTarget !== null) {
      navigate(redirectTarget, { replace: true });
    }
  }, [redirectTarget, navigate]);

  return (
    <ViewIdResolverContext.Provider value={resolveViewId}>
      {children}
    </ViewIdResolverContext.Provider>
  );
};
